import { API_URL } from '../../../../constant/API_Settings';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('AUTH_TOKEN') || ''}`,
    Accept: 'application/json',
  };
}

async function request(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });

  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }

  if (!response.ok) {
    const message = data.message || data.error || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

export function fetchSubscriptionOverview() {
  return request('/subscription/overview');
}

export function fetchSubscriptionHistory() {
  return request('/subscription/history');
}

export function fetchSubscriptionPayments() {
  return request('/subscription/payments');
}

export function fetchSubscriptionInvoices() {
  return request('/subscription/invoices');
}

export function fetchCheckoutSummary(planId) {
  return request(`/checkout/${planId}`);
}

export function saveSubscriptionAutoBilling(payload) {
  return request('/subscription/auto-billing', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function initiateSubscriptionPayment(payload) {
  return request('/initiate_payment', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function previewSubscriptionCoupon(payload) {
  return request('/subscription/coupon/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Strip paste noise (spaces, NBSP, zero-width, quotes) and uppercase for coupon codes. */
export function normalizeCouponCode(raw) {
  if (raw == null || typeof raw === 'object') {
    return '';
  }
  return String(raw)
    .replace(/[\u200B-\u200D\uFEFF\u00A0\s"'`´‘’“”]+/g, '')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .toUpperCase()
    .slice(0, 32);
}

export function verifyRazorpayPayment(payload) {
  return request('/subscription/razorpay/verify', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function initiateInvoiceOnlinePayment(invoiceId, payload = {}) {
  return request(`/subscription/invoices/${invoiceId}/pay`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function fetchInvoicePrintUrl(invoiceId) {
  return request(`/invoice-settings/${invoiceId}/print-url`);
}

export function fetchGstProfile() {
  return request('/invoice-settings/show');
}

export function saveGstProfile(payload) {
  return request('/invoice-settings/save', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

export function submitPayUForm(action, fields) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export function submitCcavenueForm(action, encryptedData, accessCode) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  const encRequest = document.createElement('input');
  encRequest.name = 'encRequest';
  encRequest.value = encryptedData;
  form.appendChild(encRequest);
  const access = document.createElement('input');
  access.name = 'access_code';
  access.value = accessCode;
  form.appendChild(access);
  document.body.appendChild(form);
  form.submit();
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

export const BILLING_MONTH_OPTIONS = [1, 3, 6, 12, 24];

export function addMonthsToDateInput(startDate, months) {
  if (!startDate) {
    return '';
  }
  const n = Math.min(36, Math.max(1, parseInt(months, 10) || 1));
  const [year, month, day] = String(startDate).split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }
  const start = new Date(year, month - 1, day);
  start.setMonth(start.getMonth() + n);
  return [
    start.getFullYear(),
    String(start.getMonth() + 1).padStart(2, '0'),
    String(start.getDate()).padStart(2, '0'),
  ].join('-');
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function normalizeAdvanceOffers(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  const byMonths = {};
  raw.forEach((row) => {
    const months = parseInt(row?.months, 10);
    const percent = Number(row?.discount_percent ?? row?.discount ?? 0);
    if (!Number.isFinite(months) || months < 1 || months > 36 || percent < 0 || percent > 100) {
      return;
    }
    byMonths[months] = { months, discount_percent: roundMoney(percent) };
  });
  return Object.keys(byMonths)
    .map(Number)
    .sort((a, b) => a - b)
    .map((months) => byMonths[months]);
}

// Kept in sync with the backend's config('petrosmart_billing.subscription_gst_percent')
// (see SubscriptionManagementService::quotePurchase). Petrosmart's subscription GST
// rate is a stable, system-wide 18%; if it ever changes, update both sides. A plan
// can override it via plan.gst_percent if the backend ever starts sending one.
const SUBSCRIPTION_GST_PERCENT = 18;

// Client-side mirror of SubscriptionManagementService::quotePurchase() used for the
// live checkout/order-summary preview before the server round-trip. payable_amount is
// GST-inclusive (taxable amount + GST on top) and must match what initiatePayment()
// actually charges, so this function has to stay numerically identical to the backend.
export function quotePlanPurchase(plan, months, coupon = null) {
  const n = Math.min(36, Math.max(1, parseInt(months, 10) || 1));
  const durationDays = Math.max(1, parseInt(plan?.duration, 10) || 30);
  const periodAmount = Number(plan?.amount) || 0;
  const monthly = plan?.monthly_amount != null
    ? Number(plan.monthly_amount)
    : (durationDays <= 31 ? periodAmount : periodAmount / (durationDays / 30));
  const gross = roundMoney(monthly * n);
  const offers = normalizeAdvanceOffers(plan?.advance_offers);
  const applied = coupon ? null : ([...offers].reverse().find((offer) => offer.months <= n) || null);
  let percent = 0;
  let discount = 0;
  let couponDiscountMonths = null;
  if (coupon) {
    percent = Number(coupon.discount_percent) || 0;
    // Match backend: percent applies only to discount_months (fallback min_months), not the full term.
    const covered = parseInt(coupon.discount_months != null ? coupon.discount_months : coupon.min_months, 10) || 1;
    couponDiscountMonths = Math.max(1, Math.min(n, covered));
    discount = roundMoney(roundMoney(monthly * couponDiscountMonths) * percent / 100);
  } else {
    percent = applied ? Number(applied.discount_percent) : 0;
    discount = roundMoney(gross * percent / 100);
  }
  const taxableAmount = roundMoney(Math.max(0, gross - discount));
  const gstPercent = Number(plan?.gst_percent) || SUBSCRIPTION_GST_PERCENT;
  const gstAmount = roundMoney(taxableAmount * gstPercent / 100);

  return {
    months: n,
    monthly_amount: roundMoney(monthly),
    gross_amount: gross,
    discount_percent: percent,
    discount_amount: discount,
    taxable_amount: taxableAmount,
    gst_percent: gstPercent,
    gst_amount: gstAmount,
    payable_amount: roundMoney(taxableAmount + gstAmount),
    applied_offer: applied,
    advance_offers: offers,
    coupon: coupon || null,
    coupon_discount_months: couponDiscountMonths,
    discount_source: coupon ? 'coupon' : (applied ? 'advance_offer' : null),
  };
}
